import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Audience, EventDay } from '../../lib/events';

export type SessionFormat = 'keynote' | 'panel' | 'workshop' | 'talk' | 'break' | 'meal' | 'networking' | 'other';
export type PartRole = 'speaker' | 'panelist' | 'moderator' | 'emcee';
export type Confirmation = 'tbc' | 'confirmed' | 'declined';
export type FollowupKey = 'confirmation' | 'bio' | 'photo' | 'title' | 'approval' | 'pheedloop';

export interface SessionPerson {
  partId: string;
  speakerId: string;
  role: PartRole;
  name: string;
  displayName: string | null;
  title: string | null;
  affiliation: string | null;
  format: 'in_person' | 'virtual' | null;
  confirmation: Confirmation;
  speakerType?: SpeakerType | null;
}

export type SpeakerType =
  'keynote' | 'speaker' | 'panelist' | 'workshop_lead' | 'moderator' | 'emcee' | 'elder' | 'youth_org' | 'industry';
export type PayeeType = 'honorarium_form' | 'vendor_setup' | 'non_resident' | 'institution_invoice';

export interface Recording {
  recorded?: boolean | null;
  subtitlesEn?: boolean;
  subtitlesFr?: boolean;
  link?: string | null;
  publishedWhere?: string | null;
  notes?: string | null;
}

export interface Deliverables {
  draftDeckAt?: string | null;
  finalDeckAt?: string | null;
  glossaryAt?: string | null;
  prepMeetingAt?: string | null;
  deckLink?: string | null;
  glossaryLink?: string | null;
  dinnerRsvp?: 'yes' | 'no' | null;
  consentToRecord?: boolean | null;
}

export interface DeliverableItem {
  key: string;
  label: string;
  due: string | null;
  done: boolean;
  overdue: boolean;
}

export interface Commitment {
  dietary?: string | null;
  schedule?: string | null;
  arrival?: string | null;
  departure?: string | null;
  accessibility?: string | null;
}

export interface Engagement {
  invitationNotes?: string | null;
  timing?: string | null;
  protocol?: string | null;
  honorariumAgreedAt?: string | null;
  route?: 'honorarium' | 'gift' | null;
  routeStatus?: 'not_started' | 'in_progress' | 'done' | null;
}

export interface AgendaSession {
  id: string;
  day: string;
  startTime: string;
  endTime: string | null;
  title: string;
  titleFr: string | null;
  description: string | null;
  descriptionFr: string | null;
  format: SessionFormat;
  room: string | null;
  audiences: string[] | null;
  hybrid: boolean;
  interpreted: boolean;
  sortOrder: number;
  notes: string | null;
  people: SessionPerson[];
  zoomLink: string | null;
  recordingPlanned: boolean;
  recording: Recording;
  interpretedEffective: boolean;
  recordingGaps: string[];
}

export interface Speaker {
  id: string;
  person: { type: 'member' | 'contact' | 'unknown'; id: string | null };
  name: string;
  email: string | null;
  displayName: string | null;
  title: string | null;
  affiliation: string | null;
  bio: string | null;
  bioFr: string | null;
  photo: string | null;
  invitedAt: string | null;
  confirmation: Confirmation;
  confirmedAt: string | null;
  format: 'in_person' | 'virtual' | null;
  approvalSentAt: string | null;
  pheedloopAt: string | null;
  inCoreForm: boolean | null;
  invitee: { id: string; audienceKey: string | null; status: string; role: string | null } | null;
  sessions: { partId: string; sessionId: string; title: string; day: string; startTime: string; role: PartRole }[];
  missing: FollowupKey[];
  speakerType: SpeakerType | null;
  payeeType: PayeeType | null;
  deliverables: Deliverables;
  deliverableItems: DeliverableItem[];
  onGrant: boolean;
  transfersCovered: boolean;
  commitment?: Commitment;
  engagement?: Engagement;
  hotelNeeded?: boolean | null;
  travel?: {
    hotel: boolean | null;
    nights: number | null;
    travelMode: string | null;
    attendanceMode: string | null;
  } | null;
  honorarium?: 'none' | 'paid' | 'declined';
  honorariumCents?: number | null;
  paymentEntryId?: string | null;
  notes?: string | null;
}

export interface Agenda {
  event: {
    id: string;
    title: string;
    titleFr: string | null;
    mode: string;
    venue: string | null;
    city: string | null;
    days: EventDay[];
    audiences: Audience[];
    contactEmail: string | null;
  };
  sessions: AgendaSession[];
  speakers: Speaker[];
  followups: { speakerId: string; name: string; missing: FollowupKey[]; labels: string[]; sessions: string[] }[];
  formats: SessionFormat[];
  roles: PartRole[];
  canSeeNeeds: boolean;
  program: { interpretation: Interpretation; deadlines: Deadlines };
  speakerTypes: Record<SpeakerType, string>;
  payeeTypes: Record<PayeeType, string>;
}

export type InterpretationMode = 'none' | 'all' | 'virtual_only' | 'selected';
export interface Interpretation {
  mode: InterpretationMode;
  captions: boolean;
  interpreterEmails: string[];
  avEmails: string[];
  notes: string | null;
}
export interface Deadlines {
  draftDeckBusinessDays: number;
  finalDeckDays: number;
  prepMeetingDays: number;
  detailsDays: number;
}

export interface Slot {
  id: string;
  day: string;
  startTime: string;
  title: string;
}

export interface Dignitary {
  id: string;
  name: string;
  office?: string | null;
  contact?: string | null;
  letterSentOn?: string | null;
  reply: 'pending' | 'accepted' | 'video' | 'declined';
  videoReceivedAt?: string | null;
  videoLink?: string | null;
  minutes?: number;
  sessionId?: string | null;
  notes?: string | null;
  slot: Slot | null;
  next: string | null;
}

export type CompetitorStatus = 'applied' | 'finalist' | 'winner' | 'not_selected' | 'withdrawn';
export interface Competitor {
  id: string;
  competitionId: string;
  name: string;
  email: string | null;
  person: { type: 'member' | 'contact'; id: string };
  projectTitle: string | null;
  status: CompetitorStatus;
  presentationOrder: number | null;
  appliedAt: string | null;
  paymentEntryId?: string | null;
  notes?: string | null;
}

export interface Competition {
  id: string;
  name: string;
  description?: string | null;
  callOpensOn?: string | null;
  deadline?: string | null;
  extendedTo?: string | null;
  reminders: { on: string; sentAt: string | null }[];
  timeCapMinutes?: number | null;
  prizeCents?: number | null;
  prizeNote?: string | null;
  sessionId?: string | null;
  finalists?: number | null;
  slot: Slot | null;
  open: boolean;
  remindersDue: string[];
  nextReminder: string | null;
  competitors: Competitor[];
  competitorCounts?: Partial<Record<CompetitorStatus, number>>;
}

export interface ProgramRecording {
  id: string;
  day: string;
  startTime: string;
  endTime: string | null;
  title: string;
  recordingPlanned: boolean;
  recording: Recording;
  gaps: string[];
}

export interface Program {
  interpretation: Interpretation;
  deadlines: Deadlines;
  dignitaries: Dignitary[];
  competitions: Competition[];
  recordings: ProgramRecording[];
  packageSentAt: string | null;
  modes: Record<InterpretationMode, string>;
  replies: Record<Dignitary['reply'], string>;
  competitorStatuses: Record<CompetitorStatus, string>;
  today: string;
}

export function useProgram(eventId: string) {
  return useQuery<Program>(
    ['event-program', eventId],
    async () => (await api.get(`/events/${eventId}/program`)).data.data,
  );
}

/** A write that returns the whole program, which then replaces the cached one. */
export function useProgramWrite<T>(eventId: string, fn: (vars: T) => Promise<{ data: { data: Program } }>) {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: (res) => {
      qc.setQueryData(['event-program', eventId], res.data.data);
      qc.invalidateQueries(['event-agenda', eventId]);
    },
  });
}

export const FORMAT_LABEL: Record<SessionFormat, string> = {
  keynote: 'Keynote',
  panel: 'Panel',
  workshop: 'Workshop',
  talk: 'Talk',
  break: 'Break',
  meal: 'Meal',
  networking: 'Networking',
  other: 'Other',
};
export const ROLE_LABEL: Record<PartRole, string> = {
  speaker: 'Speaker',
  panelist: 'Panelist',
  moderator: 'Moderator',
  emcee: 'Emcee',
};
export const CONFIRMATION_LABEL: Record<Confirmation, string> = {
  tbc: 'To be confirmed',
  confirmed: 'Confirmed',
  declined: 'Declined',
};
export const MISSING_LABEL: Record<FollowupKey, string> = {
  confirmation: 'confirmation',
  bio: 'bio',
  photo: 'photo',
  title: 'title',
  approval: 'approval email',
  pheedloop: 'PheedLoop profile',
};

/** Short names for what a speaker owes, for the "Still to get" list and the tab's opening sentence. */
export const DELIVERABLE_SHORT: Record<string, string> = {
  draft_deck: 'draft slides',
  final_deck: 'final slides',
  glossary: 'glossary',
  prep_meeting: 'prep meeting',
  consent: 'recording consent',
  dinner: 'dinner reply',
  arrival: 'arrival time',
  details: 'dietary needs and schedule',
};
export const deliverableName = (i: DeliverableItem) => DELIVERABLE_SHORT[i.key] ?? i.label.toLowerCase();

/** Is the speaker still missing something (a follow-up or an open deliverable)? */
export const missingSomething = (s: Speaker) => s.missing.length > 0 || s.deliverableItems.some((i) => !i.done);

/** "Confirmation, bio, photo and final slides": what the speaker still has to send, in one line. */
export function stillToGet(s: Speaker): string {
  const items = [
    ...s.missing.map((m) => MISSING_LABEL[m]),
    ...s.deliverableItems.filter((i) => !i.done).map(deliverableName),
  ];
  const text = items.join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The earliest open deliverable with a due date. */
export function nextDue(items: DeliverableItem[]): DeliverableItem | null {
  return items.filter((i) => !i.done && i.due).sort((a, b) => String(a.due).localeCompare(String(b.due)))[0] ?? null;
}

/** "Nov 16" */
export const shortDate = (date: string) =>
  new Date(`${date.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-CA', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Form and table classes shared by the speakers-and-agenda panels and their dialogs. */
export const field = {
  label: 'block text-[15px] leading-5 font-semibold text-ink mb-[6px]',
  hint: 'text-small text-ink-3 mt-1',
  input: 'border border-line-strong rounded-sm bg-white px-3 py-[10px] min-h-[44px] text-table text-ink w-full',
  check: 'flex items-center gap-[10px] text-[15px] leading-[22px] text-ink min-h-[32px]',
  box: 'w-[18px] h-[18px] accent-accent m-0 flex-none',
  grid: 'grid grid-cols-2 gap-x-4 gap-y-4 max-md:grid-cols-1',
  wide: 'col-span-2 max-md:col-span-1',
  /** A folded group inside a dialog. */
  fold: 'border-t border-line [&[open]>summary]:mb-3',
  summary:
    'cursor-pointer select-none py-[11px] text-[15px] leading-[22px] text-accent hover:text-ink list-none [&::-webkit-details-marker]:hidden',
  /** A select inside a table row. */
  rowSelect: 'border border-line-strong rounded-sm bg-white px-2 py-[6px] min-h-[36px] text-[15px] text-ink',
};

/** A table that becomes one block per row below 768px; cells with `data-label` show their column name. */
export const stack = {
  table: 'w-full tabular max-md:block',
  head: 'max-md:hidden',
  body: 'max-md:block',
  row: 'border-b border-line max-md:block max-md:py-3',
  cell: "max-md:block max-md:py-[2px] max-md:pr-0 max-md:[&[data-label]]:before:content-[attr(data-label)'_·_'] max-md:before:text-small max-md:before:text-ink-3",
  acts: 'max-md:flex max-md:flex-wrap max-md:gap-2 max-md:pt-2',
};

export function clock(hhmm: string | null) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'a.m.' : 'p.m.'}`;
}
export const timeRange = (s: { startTime: string; endTime: string | null }) =>
  s.endTime ? `${clock(s.startTime)} – ${clock(s.endTime)}` : clock(s.startTime);
export const printedName = (p: { displayName: string | null; name: string }) => p.displayName || p.name;

export function useAgenda(eventId: string) {
  return useQuery<Agenda>(
    ['event-agenda', eventId],
    async () => (await api.get(`/events/${eventId}/agenda`)).data.data,
  );
}

/** A write that returns the whole agenda, which then replaces the cached one. */
export function useAgendaWrite<T>(eventId: string, fn: (vars: T) => Promise<{ data: { data: Agenda } }>) {
  const qc = useQueryClient();
  return useMutation(fn, {
    onSuccess: (res) => {
      qc.setQueryData(['event-agenda', eventId], res.data.data);
      qc.invalidateQueries(['event', eventId]);
      qc.invalidateQueries(['event-program', eventId]);
    },
  });
}
